import { defineStore } from 'pinia';
import { useRoute, useRouter } from 'nuxt/app';
import _ from 'lodash';
import { format } from 'date-fns';
import { 
  IFilter, 
  IFilterOptions,
  IFilterViewModel,
  IRecordingLength,
  ITranscriptSearchFilterCondition,
  ITranscriptSearchFilterGroup, 
  ShortCallsOptions, 
  TranscriptSearchFilterOptionTypes } from '@/models/filter.model';
import { ITimePeriodFilter, ITimePeriodFilterOptions, TimePeriodComparisonType, TimePeriodType } from '@/models/timePeriod.model';
import { ITranscriptionQuery, TranscriptionQueryTypes, TranscriptionQueryOperators, ITranscriptionQueryGroup } from '@/models/transcription.model';
import { ISharedLinkPostRequest } from '@/models/sharedLink.model';
import { IAnalyticsJobFilter, IQueryStringFilter, ISharedLinkFilter, ITreeItemFilter, ObjectionCategory } from '@/models/shared.model';
import { DEFAULT_FILTER } from '@/constants/filter';
import { convertToPriorPeriod } from '@/helpers/timePeriod';
import { getQueryStringParams } from '@/helpers/utilities';
import { convertSelectedPropsToDates } from '@/helpers/timeFormatter';
import { parseCalendarDate } from '@/helpers/tenantCalendar';
import { timePeriodFilterService } from "@/services/timePeriodFilter";
import { filtersDataService } from '@/services/FiltersApi';
import { sharedLinkDataService } from '@/services/SharedLinkApi';
import { analyticsJobDataService } from '@/services/AnalyticsJobApi';
import { IScorecardFilter } from '@/models/scorecardFilter.model';

interface ITopbarFilterStore {
  filterSource: IFilter;
  timePeriodFilterSource: ITimePeriodFilter;
  queryStringFilter: IQueryStringFilter;
  sharedLinkFilter: ISharedLinkFilter | undefined;
  analyticsJobFilter: IAnalyticsJobFilter | undefined;
  filterDataSource: IFilterViewModel | undefined;
  isLoading: boolean;
  isError: boolean;
  resetFilter: boolean;
  isThresholdExceeded: boolean;
  isCachedDataset: boolean;
  cachedDateTime: Date | undefined;
}

const topbarFilterKey = "topbarFilter";
const timePeriodFilterKey = "timePeriodFilter";

export const useTopbarFilterStore = defineStore('topbarFilterStore', {
  state: (): ITopbarFilterStore => ({
    filterSource: getFilter(),
    timePeriodFilterSource: getTimePeriodFilter(),
    queryStringFilter: getQueryStringFilter(),
    sharedLinkFilter: undefined,
    analyticsJobFilter: undefined,
    filterDataSource: undefined,
    isLoading: false,
    isError: false,
    resetFilter: false,
    isThresholdExceeded: false,
    isCachedDataset: false,
    cachedDateTime: undefined
  }),
  getters: {
    filter(state): IFilter {
      // The order of properties must match the order of properties in the "IFilter" interface
      return {
        campaignIds: state.queryStringFilter.campaignId != undefined
          ? [+state.queryStringFilter.campaignId]
          : state.filterSource.campaignIds,
        teamIds: state.queryStringFilter.teamId != undefined
          ? [+state.queryStringFilter.teamId]
          : state.filterSource.teamIds,
        userIds: state.queryStringFilter.userId != undefined
          ? [+state.queryStringFilter.userId]
          : state.filterSource.userIds,
        queueIds: state.filterSource.queueIds,
        resultCodes: state.queryStringFilter.resultCode != undefined
          ? [state.queryStringFilter.resultCode]
          : state.filterSource.resultCodes,
        recordingLength: this.queryStringFilterRecordingLength ?? state.filterSource.recordingLength,
        callDirections: state.filterSource.callDirections,
        lists: state.queryStringFilter.list != undefined
          ? [state.queryStringFilter.list]
          : state.filterSource.lists,
        daysOfWeek: state.filterSource.daysOfWeek,
        shortCalls: state.filterSource.shortCalls,
        transcriptSearch: state.filterSource.transcriptSearch,
        isScored: state.filterSource.isScored,
        scorecardFilter: state.filterSource.scorecardFilter,
        isSuccess: state.queryStringFilter.isSuccess ?? state.filterSource.isSuccess,
        firstName: state.filterSource.firstName,
        lastName: state.filterSource.lastName,
        phoneNumber: state.filterSource.phoneNumber,
        postalCode: state.filterSource.postalCode,
        referenceId: state.filterSource.referenceId,
        topics: state.queryStringFilter.topic
          ? [state.queryStringFilter.topic.replace(/\s/g, '_')]
          : state.filterSource.topics,
        overallSentiments: state.queryStringFilter.sentiment
          ? [state.queryStringFilter.sentiment]
          : state.filterSource.overallSentiments,
        peakSentiments: state.queryStringFilter.peakSentiment
          ? [state.queryStringFilter.peakSentiment]
          : state.filterSource.peakSentiments,
        endSentiments: state.queryStringFilter.endSentiment
          ? [state.queryStringFilter.endSentiment]
          : state.filterSource.endSentiments,
        objections: this.queryStringFilterObjections ?? state.filterSource.objections,
        dataSupplierIds: state.queryStringFilter.dataSupplierId != undefined
          ? [+state.queryStringFilter.dataSupplierId]
          : state.filterSource.dataSupplierIds,
      };
    },
    filterDuration(): string {
      return this.filter.recordingLength
        ? `${this.filter.recordingLength.min}-${this.filter.recordingLength.max}`
        : '';
    },
    filterOptions(): IFilterOptions {
      const duration = this.filterDuration;
      const filter = this.filter;

      return {
        campaignIds: filter.campaignIds,
        teamIds: filter.teamIds,
        queueIds: filter.queueIds,
        userIds: filter.userIds,
        resultCodes: filter.resultCodes,
        durations: duration ? [duration] : undefined,
        callDirections: filter.callDirections,
        lists: filter.lists ? filter.lists.map(item => item.replace(/_/g, " ")) : undefined,
        daysOfWeek: filter.daysOfWeek,
        excludeShortCalls: filter.shortCalls === ShortCallsOptions.showOnlyAiProcessed ? true : undefined,
        onlyShortCalls: filter.shortCalls === ShortCallsOptions.showOnlyAiNotProcessed ? true : undefined,
        isScored: filter.isScored,
        isSuccess: filter.isSuccess,
        firstName: filter.firstName,
        lastName: filter.lastName,
        phoneNumber: filter.phoneNumber,
        postalCode: filter.postalCode,
        referenceId: filter.referenceId,
        topics: filter.topics ? filter.topics.map(item => item.replace(/_/g, " ")) : undefined,
        overallSentiments: filter.overallSentiments,
        peakSentiments: filter.peakSentiments,
        endSentiments: filter.endSentiments,
        objectionNames: filter.objections?.filter(item => item.childItems.length > 0).map(item => item.childItems).flat().map(item => item.replace(/_/g, " ")) ?? undefined,
        objectionCategories: filter.objections?.map(item => item.value.replace(/_/g, " ")) ?? undefined,
        dataSupplierIds: filter.dataSupplierIds,
        queryGroups: this.transcriptionSearchQueryGroupOptions,
        scorecardFilter: this.scorecardFilterOptions,
      };
    },
    timePeriodFilter(state) {
      return {
        startDate: state.queryStringFilter.startDate
          ? state.queryStringFilter.startDate
          : state.timePeriodFilterSource.startDate,
        endDate: state.queryStringFilter.endDate
          ? state.queryStringFilter.endDate
          : state.timePeriodFilterSource.endDate,
        previousStartDate: state.timePeriodFilterSource.previousStartDate,
        previousEndDate: state.timePeriodFilterSource.previousEndDate,
        selectedPeriod: state.queryStringFilter.startDate && state.queryStringFilter.endDate
          ? TimePeriodType.custom
          : state.timePeriodFilterSource.selectedPeriod,
        comparisonType: state.timePeriodFilterSource.comparisonType,
      };
    },
    timePeriodFilterOptions(): ITimePeriodFilterOptions {
      const timePeriodFilter = this.timePeriodFilter;
      return {
        startDate: format(timePeriodFilter.startDate, "yyyy-MM-dd"),
        endDate: format(timePeriodFilter.endDate, "yyyy-MM-dd"),
        previousStartDate: format(timePeriodFilter.previousStartDate, "yyyy-MM-dd"),
        previousEndDate: format(timePeriodFilter.previousEndDate, "yyyy-MM-dd"),
      }
    },
    transcriptionSearchQueryGroupOptions(state): ITranscriptionQueryGroup[] | undefined {
      const queryGroups: ITranscriptionQueryGroup[] = [];

      const transcriptSearchQueries = state.filterSource.transcriptSearch.options
        ?.filter(option => option.type === TranscriptSearchFilterOptionTypes.condition && (option.value as ITranscriptSearchFilterCondition).text)
        ?.map(option => {
          const condition = option.value as ITranscriptSearchFilterCondition;
          return {
            text: condition.text,
            operator: condition.operator,
            type: TranscriptionQueryTypes.text,
            conjuction: state.filterSource.transcriptSearch.conjunction,
          } as ITranscriptionQuery;
        });

      if (transcriptSearchQueries && transcriptSearchQueries.length) {
        queryGroups.push(
          { 
            queries: transcriptSearchQueries,
            groupConjuction: state.filterSource.transcriptSearch.conjunction,
          });
      }

      const transcriptSearchGroups = state.filterSource.transcriptSearch.options
        ?.filter(option => option.type === TranscriptSearchFilterOptionTypes.group && (option.value as ITranscriptSearchFilterGroup)?.conditions?.some(condition => condition.text))
        ?.map(option => {
          const group = option.value as ITranscriptSearchFilterGroup;
          return {
            queries: group.conditions.filter(condition => condition.text)?.map(condition => ({
              text: condition.text,
              operator: condition.operator,
              type: TranscriptionQueryTypes.text,
              conjuction: group.conjunction,
            } as ITranscriptionQuery)),
            groupConjuction: state.filterSource.transcriptSearch.conjunction,
          } as ITranscriptionQueryGroup;
        });

      if (transcriptSearchGroups && transcriptSearchGroups.length) {
        queryGroups.push(...transcriptSearchGroups);
      }
      
      if (state.queryStringFilter?.interactionsSearch) {
        queryGroups.push({
            queries: [{
                text: state.queryStringFilter?.interactionsSearch,
                operator: TranscriptionQueryOperators.contains,
                type: TranscriptionQueryTypes.metadata,
            }]
        });
      }

      return queryGroups.length ? queryGroups : undefined;
    },
    scorecardFilterOptions(state) : IScorecardFilter | undefined {
      return (state.filterSource.scorecardFilter?.options ?? []).some(option => !!option?.value?.scorecardId)
        ? state.filterSource.scorecardFilter
        : undefined;
    },
    hasAppliedSentimentFilters(): boolean {
      return (this.filter?.overallSentiments ?? []).length > 0 ||
       (this.filter.peakSentiments ?? []).length > 0 ||
       (this.filter.endSentiments ?? []).length > 0;
    },
    hasAppliedQueryStringFilter(state) {
      return state.queryStringFilter && Object.values(state.queryStringFilter).some(el => el !== undefined);
    },
    queryStringFilterObjections(state): ITreeItemFilter[] | undefined {
      let objectionCategories = state.queryStringFilter.objectionCategory
      ? [state.queryStringFilter.objectionCategory] as ObjectionCategory[]
      : undefined;

      const objectionName = state.queryStringFilter.objectionName?.replace(/\s/g, '_');

      if (objectionName && !objectionCategories) {
        objectionCategories = state.filterDataSource?.objections?.filter(o => o.childItems?.some(c => c.value === objectionName))?.map(o => o.value as ObjectionCategory);
      }

      if (objectionCategories && objectionCategories.length) {
        return objectionCategories.map(category => ({
          value: category,
          selectedAll: objectionName ? false : true,
          childItems: objectionName ? [objectionName] : [],
        }));
      }

      return undefined;
    },
    queryStringFilterRecordingLength(state): IRecordingLength | undefined {
      const duration = state.queryStringFilter.duration;
      return duration
        ? {
            min: +duration.split('-')[0],
            max: +duration.split('-')[1],
          }
        : undefined;
    },
    async filterDataAsync(state): Promise<IFilterViewModel | undefined> {
      if (!state.filterDataSource) {
        try {
          state.isError = false;
          state.isLoading = true;
          state.filterDataSource = await filtersDataService.getFiltersAsync();
        }
        catch(error) {
          state.isError = true;
          throw error;
        }
        finally {
          state.isLoading = false;
        }
      }

      return state.filterDataSource;
    } 
  },
  actions: {
    updateSelectedPeriod ({ selected, customPeriod }) {
      if (customPeriod) {
        const [ previousStartDate, previousEndDate] = convertToPriorPeriod(customPeriod);
        this.timePeriodFilterSource = {
          startDate: customPeriod[0],
          endDate: customPeriod[1],
          previousStartDate,
          previousEndDate,
          selectedPeriod: TimePeriodType.custom,
          comparisonType: TimePeriodComparisonType.fullPeriod,
        };
      }
      else {
        const { startDate, endDate, previousStartDate, previousEndDate} = timePeriodFilterService.calculateDates(selected);
        this.timePeriodFilterSource = {
          startDate,
          endDate,
          previousStartDate,
          previousEndDate,
          selectedPeriod: TimePeriodType[selected],
          comparisonType: TimePeriodComparisonType.fullPeriod,
        };
      }

      this.setTimePeriodFilterToStorage();
    },
    updateComparisonType({ selected }) {
      const { startDate, endDate, previousStartDate, previousEndDate} = timePeriodFilterService.calculateDates(
        this.timePeriodFilter.selectedPeriod,
        selected === TimePeriodComparisonType.partialPeriod);

      this.timePeriodFilterSource = {
        ...this.timePeriodFilter,
        startDate,
        endDate,
        previousStartDate,
        previousEndDate,
        comparisonType: TimePeriodComparisonType[selected],
      };

      this.setTimePeriodFilterToStorage();
    },
    setFilter(filter: IFilter, writeToStorage: boolean = true) {
      this.filterSource = _.cloneDeep(filter);
      if (writeToStorage) {
        this.setFilterToStorage();
      }
    },
    setTimePeriodFilter(timePeriodFilter: ITimePeriodFilter) {
      this.timePeriodFilterSource = _.cloneDeep(timePeriodFilter);
    },
    setQueryStringFilter(queryStringFilter: IQueryStringFilter) {
      this.queryStringFilter = _.cloneDeep(queryStringFilter);
    },
    setQueryStringFilterToUrl() {
      const router = useRouter();
      const queryStringOptions = {
        ...router.currentRoute.value.query,
        campaignId: this.queryStringFilter?.campaignId,
        teamId: this.queryStringFilter?.teamId,
        userId: this.queryStringFilter?.userId,
        interactionsSearch: this.queryStringFilter?.interactionsSearch,
        isSuccess: this.queryStringFilter?.isSuccess,
        objectionName: this.queryStringFilter?.objectionName,
        objectionCategory: this.queryStringFilter?.objectionCategory,
        sentiment: this.queryStringFilter?.sentiment,
        peakSentiment: this.queryStringFilter?.peakSentiment,
        endSentiment: this.queryStringFilter?.endSentiment,
        topic: this.queryStringFilter?.topic,
        duration: this.queryStringFilter?.duration,
        resultCode: this.queryStringFilter?.resultCode,
        startDate: this.queryStringFilter?.startDate,
        endDate: this.queryStringFilter?.endDate,
        list: this.queryStringFilter?.list,
        dataSupplierId: this.queryStringFilter?.dataSupplierId
      };

      const queryString = getQueryStringParams(queryStringOptions);
      history.pushState({}, "", router.currentRoute.value.path + (queryString ? "?" + queryString : ""));
    },
    setInteractionsSearch(value: string) {
      this.queryStringFilter.interactionsSearch = value ? value : undefined;
    },
    resetFilter() {
      this.filterSource = _.cloneDeep(DEFAULT_FILTER);
    },
    resetFilterData() {
      this.filterDataSource = undefined;
    },
    resetQueryStringFilter() {
      this.queryStringFilter = {};
    },
    resetSharedLinkFilter() {
      this.sharedLinkFilter = undefined;

      const router = useRouter();
      const queryStringOptions = {
        ...router.currentRoute.value.query,
        link: undefined,
      };

      const queryString = getQueryStringParams(queryStringOptions);
      history.pushState({}, "", router.currentRoute.value.path + (queryString ? "?" + queryString : ""));
    },
    resetAnalyticsJobFilter() {
      this.analyticsJobFilter = undefined;

      const router = useRouter();
      const queryStringOptions = {
        ...router.currentRoute.value.query,
        job: undefined,
      };

      const queryString = getQueryStringParams(queryStringOptions);
      history.pushState({}, "", router.currentRoute.value.path + (queryString ? "?" + queryString : ""));
    },
    setFilterToStorage() {
      sessionStorage.setItem(topbarFilterKey, JSON.stringify(this.filterSource));
    },
    setTimePeriodFilterToStorage() {
      sessionStorage.setItem(timePeriodFilterKey, JSON.stringify(this.timePeriodFilterSource));
    },
    async setSharedLinkFilterAsync(linkId: string) {
      if (!linkId || this.sharedLinkFilter && this.sharedLinkFilter.linkId === linkId) return;
    
      try {        
        const request: ISharedLinkPostRequest = { id: linkId };
        const response = await sharedLinkDataService.postAsync(request);
        
        this.sharedLinkFilter = {
          advancedFilter: response.filterData.advancedFilter,
          timePeriodFilter: convertSelectedPropsToDates(response.filterData.timePeriodFilter, ['startDate', 'endDate', 'previousStartDate', 'previousEndDate']),
          linkId,
          quickFilterId: response.quickFilterId
        } as ISharedLinkFilter;
        this.sharedLinkFilter.timePeriodFilter.selectedPeriod = TimePeriodType.custom;

        this.setFilter(this.sharedLinkFilter.advancedFilter, false);
        this.setTimePeriodFilter(this.sharedLinkFilter.timePeriodFilter);
      } 
      catch (error: any) {
        if (error.response?.status === 401) {
          throw error;
        }
      }
    },
    async setAnalyticsJobFilterAsync(jobId: string) {
      if (!jobId || this.analyticsJobFilter && this.analyticsJobFilter.jobId === jobId) return;
    
      try {        
        const job = await analyticsJobDataService.getAsync(jobId);
        
        this.analyticsJobFilter = {
          advancedFilter: job.input,
          timePeriodFilter: { 
            // Job dates are tenant-local calendar dates: keep the day, don't shift by the browser zone.
            startDate: parseCalendarDate(job.startDate ? job.startDate : ''), 
            endDate: parseCalendarDate(job.endDate ? job.endDate : ''), 
            previousStartDate: parseCalendarDate(job.previousStartDate ? job.previousStartDate : ''), 
            previousEndDate: parseCalendarDate(job.previousEndDate ? job.previousEndDate : ''), 
            selectedPeriod: TimePeriodType.custom 
          } as ITimePeriodFilter,
          jobId,
          quickFilterId: job.quickFilterId
        } as IAnalyticsJobFilter;
        this.setFilter(this.analyticsJobFilter.advancedFilter, false);
        this.setTimePeriodFilter(this.analyticsJobFilter.timePeriodFilter);
      } 
      catch (error: any) {
        if (error.response?.status === 401) {
          throw error;
        }
      }
    },
  },
});

const getDefaultTimePeriodFilter = () : ITimePeriodFilter => {
  const selectedPeriod = timePeriodFilterService.defaultPeriod();
  const { startDate, endDate, previousStartDate, previousEndDate} = timePeriodFilterService.calculateDates(selectedPeriod);
  return {
    startDate,
    endDate,
    previousStartDate,
    previousEndDate,
    selectedPeriod,
    comparisonType: TimePeriodComparisonType.fullPeriod
  } as ITimePeriodFilter;
}

const getFilter = () => {
  const getFilterString = sessionStorage.getItem(topbarFilterKey);
  return getFilterString
    ? JSON.parse(getFilterString)
    : _.cloneDeep(DEFAULT_FILTER);
};

const getTimePeriodFilter = () => {
  const dateProperties = ['startDate', 'endDate', 'previousStartDate', 'previousEndDate'];
  const timePeriodFilterString = sessionStorage.getItem(timePeriodFilterKey);
  if (timePeriodFilterString) {
    return JSON.parse(timePeriodFilterString, (key, value) => dateProperties.includes(key) ? new Date(value) : value);
  }

  return getDefaultTimePeriodFilter();
};

const getQueryStringFilter = () => {
  const route = useRoute();
  return {
    campaignId: route.query.campaignId != undefined && Number.isFinite(route.query.campaignId) ? +route.query.campaignId : undefined,
    teamId: route.query.teamId != undefined && Number.isFinite(route.query.teamId) ? +route.query.teamId : undefined,
    userId: route.query.userId != undefined && Number.isFinite(route.query.userId) ? +route.query.userId : undefined,
    interactionsSearch: route.query.interactionsSearch ? route.query.interactionsSearch as string : undefined,
    isSuccess: route.query.isSuccess === undefined ? undefined : route.query.isSuccess?.toString().toLocaleLowerCase() as unknown as boolean,
    objectionName: route.query.objectionName ? route.query.objectionName as string : undefined,
    objectionCategory: route.query.objectionCategory ? route.query.objectionCategory as string : undefined,
    sentiment: route.query.sentiment ? route.query.sentiment as string : undefined,
    peakSentiment: route.query.peakSentiment ? route.query.peakSentiment as string : undefined,
    endSentiment: route.query.endSentiment ? route.query.endSentiment as string : undefined,
    topic: route.query.topic ? route.query.topic as string : undefined,
    startDate: route.query.startDate ? parseCalendarDate(route.query.startDate.toString()) : undefined,
    endDate: route.query.endDate ? parseCalendarDate(route.query.endDate.toString()) : undefined,
    resultCode: route.query.resultCode ? route.query.resultCode as string : undefined,
    list: route.query.list ? route.query.list as string : undefined,
    dataSupplierId: route.query.dataSupplierId != undefined && Number.isFinite(route.query.dataSupplierId) ? +route.query.dataSupplierId : undefined,
  };
};